import { useCallback, useEffect, useMemo, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { AlertTriangle, Eye, EyeOff, ImageOff, Loader2, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { NOM_DU_TYPE_PLURIEL, type TypeDActivite } from '../../lib/seo/stage';
import {
  compterParStatut,
  compterParType,
  estActif,
  filtrerStages,
  FILTRES_PAR_DEFAUT,
  libelleAge,
  libelleSessions,
  peutEtreSupprime,
  type Filtres,
  type FiltreStatut,
  type FiltreType,
} from '../../lib/stages/catalogue';
import { useStageStore, type Stage } from '../../stores/stageStore';
import StageFormModal from './components/stages/StageFormModal';
import StageDeleteModal from './components/stages/StageDeleteModal';

/*
  Le catalogue des stages : ce qu'on programme ensuite, semaine par semaine,
  dans l'outil de programmation. Le prix n'est pas ici : il vit sur chaque
  session.
*/

const TYPES: FiltreType[] = ['tous', 'plaine', 'stage', 'sejour'];
const LIBELLE_TYPE: Record<FiltreType, string> = { tous: 'Tous les types', ...NOM_DU_TYPE_PLURIEL };
const LIBELLE_TYPE_COURT: Record<TypeDActivite, string> = { plaine: 'Plaine', stage: 'Stage', sejour: 'Séjour' };
const COULEUR_TYPE: Record<TypeDActivite, string> = {
  plaine: 'bg-sky-50 text-sky-700',
  stage: 'bg-violet-50 text-violet-700',
  sejour: 'bg-amber-50 text-amber-700',
};

const STATUTS: FiltreStatut[] = ['tous', 'actifs', 'inactifs', 'sans_image'];
const LIBELLE_STATUT: Record<FiltreStatut, string> = {
  tous: 'Tous',
  actifs: 'Actifs',
  inactifs: 'Inactifs',
  sans_image: 'Sans image',
};

// Le temps pendant lequel la ligne tout juste enregistree reste eclairee.
const DUREE_DE_MISE_EN_LUMIERE = 2500;

const Pastille = ({ actif, onClick, children }: { actif: boolean; onClick: () => void; children: React.ReactNode }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={actif}
    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
      actif ? 'bg-primary-600 border-primary-600 text-white' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
    }`}
  >
    {children}
  </button>
);

const AdminStagesPage = () => {
  const { stages, chargement, erreurDeChargement, enregistrement, fetchStages, setActive } = useStageStore();

  const [filtres, setFiltres] = useState<Filtres>(FILTRES_PAR_DEFAUT);
  // `undefined` : fenetre fermee ; `null` : nouveau stage.
  const [enEdition, setEnEdition] = useState<Stage | null | undefined>(undefined);
  const [aSupprimer, setASupprimer] = useState<Stage | null>(null);
  const [enLumiere, setEnLumiere] = useState<string | null>(null);

  useEffect(() => {
    fetchStages();
  }, [fetchStages]);

  const visibles = useMemo(() => filtrerStages(stages, filtres), [stages, filtres]);
  const parType = useMemo(() => compterParType(stages, filtres), [stages, filtres]);
  const parStatut = useMemo(() => compterParStatut(stages, filtres), [stages, filtres]);

  /*
    La confirmation visuelle : la ligne s'eclaire et vient a l'ecran. Une
    ligne que les filtres cacheraient serait introuvable — ils sont alors
    remis a zero.
  */
  const mettreEnLumiere = useCallback(
    (stage: Stage) => {
      if (filtrerStages([stage], filtres).length === 0) setFiltres(FILTRES_PAR_DEFAUT);
      setEnLumiere(stage.id);
    },
    [filtres]
  );

  useEffect(() => {
    if (!enLumiere) return;
    const ligne = document.querySelector(`[data-stage-id="${enLumiere}"]`);
    ligne?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const minuterie = window.setTimeout(() => setEnLumiere(null), DUREE_DE_MISE_EN_LUMIERE);
    return () => window.clearTimeout(minuterie);
  }, [enLumiere]);

  const surEnregistrement = (stage: Stage, cree: boolean) => {
    setEnEdition(undefined);
    toast.success(cree ? `Stage « ${stage.title} » créé` : `Stage « ${stage.title} » modifié`);
    mettreEnLumiere(stage);
  };

  const surSuppression = (stage: Stage) => {
    setASupprimer(null);
    toast.success(`Stage « ${stage.title} » supprimé`);
  };

  /*
    Desactiver est sans danger et se defait : pas de confirmation, mais la
    notification porte un bouton « Annuler ».
  */
  const basculerActif = async (stage: Stage, active: boolean) => {
    try {
      const modifie = await setActive(stage.id, active);
      // Sans toucher aux filtres : la ligne peut en sortir, c'est ce qu'on a demande.
      setEnLumiere(modifie.id);
      toast.success(
        (t) => (
          <span className="flex items-center gap-3">
            <span>
              « {stage.title} » {active ? 'réactivé' : 'désactivé'}
            </span>
            <button
              type="button"
              className="text-sm font-medium text-primary-600 hover:underline"
              onClick={() => {
                toast.dismiss(t.id);
                basculerActif(modifie, !active);
              }}
            >
              Annuler
            </button>
          </span>
        ),
        { duration: 6000 }
      );
    } catch (error) {
      console.error('Error toggling stage:', error);
      toast.error(`« ${stage.title} » n’a pas pu être ${active ? 'réactivé' : 'désactivé'}. Réessayez.`);
    }
  };

  const changerFiltre = <K extends keyof Filtres>(cle: K, valeur: Filtres[K]) =>
    setFiltres((f) => ({ ...f, [cle]: valeur }));

  // Le spinner plein ecran ne sert qu'au premier chargement.
  if (chargement && stages.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div>
      <Toaster position="top-right" />

      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des stages</h1>
          <p className="text-gray-600 mt-1">
            Le catalogue des activités. Les dates et les prix se règlent dans l’outil de programmation.
          </p>
        </div>
        <button onClick={() => setEnEdition(null)} className="btn-primary flex items-center flex-shrink-0">
          <Plus className="h-5 w-5 mr-2" />
          Nouveau stage
        </button>
      </div>

      {erreurDeChargement && (
        <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg flex items-start">
          <AlertTriangle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          <p>{erreurDeChargement}</p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-md p-4 mb-4 space-y-3">
        <div className="relative max-w-sm">
          <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="search"
            value={filtres.recherche}
            onChange={(e) => changerFiltre('recherche', e.target.value)}
            placeholder="Rechercher un stage…"
            aria-label="Rechercher un stage par titre"
            className="form-input pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrer par type">
          {TYPES.filter((t) => t === 'tous' || parType[t] > 0 || filtres.type === t).map((t) => (
            <Pastille key={t} actif={filtres.type === t} onClick={() => changerFiltre('type', t)}>
              {LIBELLE_TYPE[t]} ({parType[t]})
            </Pastille>
          ))}
        </div>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrer par statut">
          {STATUTS.map((s) => (
            <Pastille key={s} actif={filtres.statut === s} onClick={() => changerFiltre('statut', s)}>
              {LIBELLE_STATUT[s]} ({parStatut[s]})
            </Pastille>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stage</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Âge</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sessions
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {visibles.map((stage) => {
                const actif = estActif(stage);
                const type = stage.type_activite ?? 'stage';
                const supprimable = peutEtreSupprime(stage);
                return (
                  <tr
                    key={stage.id}
                    data-stage-id={stage.id}
                    className={`transition-colors duration-700 ${
                      enLumiere === stage.id ? 'bg-green-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div
                        className={`h-12 w-12 rounded-lg bg-gray-100 overflow-hidden ${actif ? '' : 'opacity-50'}`}
                      >
                        {stage.image_url ? (
                          <img src={stage.image_url} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <div className="h-full w-full flex items-center justify-center" title="Sans image">
                            <ImageOff className="h-5 w-5 text-gray-300" />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 max-w-md">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-sm font-medium ${actif ? 'text-gray-900' : 'text-gray-500'}`}>
                          {stage.title}
                        </span>
                        {!actif && (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">Inactif</span>
                        )}
                        {!stage.image_url && (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-amber-50 text-amber-700">
                            Sans image
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 truncate" title={stage.description}>
                        {stage.description}
                      </p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${COULEUR_TYPE[type]}`}>
                        {LIBELLE_TYPE_COURT[type]}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {libelleAge(stage.age_min, stage.age_max)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={stage.nb_sessions_a_venir > 0 ? 'text-gray-900' : 'text-gray-400'}>
                        {libelleSessions(stage)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end items-center gap-3">
                        <button
                          onClick={() => setEnEdition(stage)}
                          className="text-primary-600 hover:text-primary-900"
                          aria-label={`Modifier ${stage.title}`}
                          title="Modifier"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => basculerActif(stage, !actif)}
                          className="text-gray-500 hover:text-gray-800 disabled:opacity-40"
                          aria-label={`${actif ? 'Désactiver' : 'Réactiver'} ${stage.title}`}
                          title={actif ? 'Désactiver' : 'Réactiver'}
                          disabled={enregistrement}
                        >
                          {actif ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={() => setASupprimer(stage)}
                          className="text-red-600 hover:text-red-900 disabled:text-gray-300 disabled:cursor-not-allowed"
                          aria-label={`Supprimer ${stage.title}`}
                          title={
                            supprimable
                              ? 'Supprimer'
                              : 'Ce stage a déjà eu des sessions : désactivez-le plutôt que de le supprimer'
                          }
                          disabled={!supprimable}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {visibles.length === 0 && (
            <div className="p-10 text-center text-sm text-gray-500">
              {stages.length === 0 ? (
                'Aucun stage pour le moment.'
              ) : (
                <>
                  Aucun stage ne correspond à ces filtres.{' '}
                  <button
                    type="button"
                    onClick={() => setFiltres(FILTRES_PAR_DEFAUT)}
                    className="text-primary-600 hover:underline"
                  >
                    Tout afficher
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {enEdition !== undefined && (
        <StageFormModal stage={enEdition} onClose={() => setEnEdition(undefined)} onSaved={surEnregistrement} />
      )}

      {aSupprimer && (
        <StageDeleteModal stage={aSupprimer} onClose={() => setASupprimer(null)} onDeleted={surSuppression} />
      )}
    </div>
  );
};

export default AdminStagesPage;
